export const useForm = () => {
	const { locale } = useI18n();
	const { $executeRecaptcha } = useNuxtApp();
	const config = useRuntimeConfig();

	const submitForm = async (
		formData: FormData,
		formName: string,
		extraQueryData: Record<string, string | number> | null = null,
	): Promise<FormApiResponse> => {
		try {
			formData.append('language', locale.value);

			const token = await $executeRecaptcha();
			if (token) {
				formData.append('g-recaptcha-response', token);
			}

			const result = await $fetch<FormApiResponse>(config.public.apiBaseURL + '/' + formName, {
				method: 'POST',
				body: formData,
				query: { language: locale.value, ...extraQueryData },
			});

			if (result.status === 'error') {
				return { status: 'error', errors: result.errors };
			} else {
				return { status: 'success', redirect: result.redirect };
			}
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error('Form submission error:', error);

			return {
				status: 'error',
				errors: [{ message: 'submitError', translationKey: 'submitError', field: null }],
			};
		}
	};

	const prepareSubmitData = (form: FormField[]) => {
		const formData = new FormData();
		form.forEach((item: FormField) => {
			const current = { ...item };
			if (isRef(current.value)) {
				current.value = current.value.value as string;
			}

			if (Array.isArray(current.value)) {
				current.value.forEach((value: string) => {
					formData.append(`${current.name}[]`, value);
				});

				return;
			}

			formData.append(current.name, current.value ?? '');
		});

		return formData;
	};

	const setErrors = (state: Ref<{ errors: string[] }>, response: FormApiResponse, form: FormField[]) => {
		if (!response.errors || !response.errors.length) {
			return;
		}

		response.errors.forEach((error) => {
			const field = form.find((item) => item.name === error.field);
			if (field) {
				field.error = error.translationKey || error.message;
			} else {
				state.value.errors.push(error.translationKey || error.message);
			}
		});
	};

	const clearErrors = (state: Ref<{ errors: string[] }>, form: FormField[]) => {
		state.value.errors = [];

		form.forEach((field) => {
			if (field.error) {
				field.error = '';
			}
		});
	};

	const resetForm = (
		form: FormField[],
		contactform: Ref<HTMLFormElement | undefined>,
		state: Ref<{ errors: string[] }>,
	): void => {
		form.forEach((field: FormField) => {
			field.value = null;
		});
		clearErrors(state, form);
		contactform.value?.reset();
	};

	return {
		submitForm,
		prepareSubmitData,
		setErrors,
		clearErrors,
		resetForm,
	};
};
